import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Copy, Check, Moon, Sun, Clock, FileText, Sparkles, Edit3, CheckCircle, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import Logo from '../../components/common/Logo';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import apiService, { convertReportToFrontend } from '../../services/api';

const AdminReportDetailPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState('dados');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar dados do backend
  useEffect(() => {
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiService.getReport(reportId);
        const converted = convertReportToFrontend(data);
        setReport(converted);
      } catch (err) {
        console.error('Erro ao carregar relatório:', err);
        setError('Relatório não encontrado.');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [reportId]);

  const copyLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/report/${reportId}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusConfig = {
    pending_form: { label: 'Aguardando formulário', icon: Clock, color: 'text-gray-600 bg-gray-100' },
    pending_routine: { label: 'Registrando rotina', icon: Clock, color: 'text-yellow-600 bg-yellow-100' },
    pending_analysis: { label: 'Aguardando análise', icon: FileText, color: 'text-blue-600 bg-blue-100' },
    pending_approval: { label: 'Aguardando aprovação', icon: AlertCircle, color: 'text-purple-600 bg-purple-100' },
    approved: { label: 'Aprovado', icon: CheckCircle, color: 'text-green-600 bg-green-100' }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4FF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#8F7AC5] animate-spin" />
      </div>
    );
  }

  // Error state
  if (error || !report) {
    return (
      <div className="min-h-screen bg-[#FBF4FF] flex flex-col items-center justify-center">
        <p className="text-[#6B5B8C] mb-4">{error || 'Relatório não encontrado'}</p>
        <Button onClick={() => navigate('/admin/dashboard')} className="bg-[#8F7AC5]">
          Voltar ao Dashboard
        </Button>
      </div>
    );
  }

  const status = statusConfig[report.status] || statusConfig.pending_routine;
  const StatusIcon = status.icon;

  return (
    <div className="min-h-screen bg-[#FBF4FF]">
      {/* Header */}
      <header className="bg-white border-b border-[#FAE0D5] px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <Logo variant="v3SonoRotina" size="sm" />
          </div>
          <div className="flex items-center gap-3">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
              <StatusIcon className="w-4 h-4" />
              {status.label}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Report Header */}
        <motion.div
          className="bg-gradient-to-r from-[#8F7AC5] to-[#D98DAE] rounded-3xl p-8 text-white mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h1 className="text-3xl font-bold mb-2">Relatório de {report.babyName}</h1>
              <p className="text-white/80">
                {report.babyAge} meses • Mãe: {report.motherName} • Criado em {report.createdAt}
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                variant="secondary"
                className="bg-white/20 hover:bg-white/30 text-white border-0"
                onClick={copyLink}
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copiado!' : 'Copiar Link do Registro'}
              </Button>
              {report.sleepPlan ? (
                <Button
                  className="bg-white text-[#8F7AC5] hover:bg-white/90"
                  onClick={() => navigate(`/admin/reports/${reportId}/plan`)}
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Ver/Editar Plano
                </Button>
              ) : (
                <Button
                  className="bg-white text-[#8F7AC5] hover:bg-white/90"
                  onClick={() => navigate(`/admin/reports/${reportId}/plan`)}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Plano
                </Button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-white rounded-2xl p-1 mb-6 w-full justify-start">
            <TabsTrigger value="dados" className="rounded-xl data-[state=active]:bg-[#8F7AC5] data-[state=active]:text-white">
              Dados
            </TabsTrigger>
            <TabsTrigger value="rotina" className="rounded-xl data-[state=active]:bg-[#8F7AC5] data-[state=active]:text-white">
              Rotina (5 dias)
            </TabsTrigger>
            <TabsTrigger value="plano" className="rounded-xl data-[state=active]:bg-[#8F7AC5] data-[state=active]:text-white">
              Plano Gerado
            </TabsTrigger>
          </TabsList>

          {/* Tab: Dados */}
          <TabsContent value="dados">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Dados do Bebê */}
              <motion.div
                className="bg-white rounded-3xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h3 className="font-semibold text-[#30244F] mb-4 flex items-center gap-2">
                  <Moon className="w-5 h-5 text-[#8F7AC5]" />
                  Dados do Bebê
                </h3>
                <div className="space-y-3">
                  {[
                    { label: 'Nome', value: report.babyName },
                    { label: 'Idade', value: `${report.babyAge} meses` },
                    { label: 'Nasceu com', value: `${report.birthWeeks} semanas` },
                    { label: 'Data de nascimento', value: report.birthDate }
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-[#FAE0D5] last:border-0">
                      <span className="text-[#9890A8]">{item.label}</span>
                      <span className="text-[#30244F] font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Dados da Família */}
              <motion.div
                className="bg-white rounded-3xl p-6 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="font-semibold text-[#30244F] mb-4">Dados da Família</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Mãe', value: report.motherName },
                    { label: 'Pai', value: report.fatherName },
                    { label: 'E-mail', value: report.email },
                    { label: 'WhatsApp', value: report.whatsapp }
                  ].map((item, i) => (
                    <div key={i} className="flex justify-between py-2 border-b border-[#FAE0D5] last:border-0">
                      <span className="text-[#9890A8]">{item.label}</span>
                      <span className="text-[#30244F] font-medium">{item.value}</span>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Questionário Inicial */}
              <motion.div
                className="bg-white rounded-3xl p-6 shadow-sm lg:col-span-2"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <h3 className="font-semibold text-[#30244F] mb-4">Questionário Inicial</h3>
                <div className="grid lg:grid-cols-2 gap-6">
                  {[
                    { label: 'Maior dificuldade', value: report.initialForm?.sleepDifficulty },
                    { label: 'Expectativas', value: report.initialForm?.expectations },
                    { label: 'Tentativas anteriores', value: report.initialForm?.previousAttempts },
                    { label: 'Como adormece', value: report.initialForm?.howBabySleeps },
                    { label: 'Onde dorme', value: report.initialForm?.sleepLocation },
                    { label: 'Alimentação', value: report.initialForm?.feedingType },
                    { label: 'Desenvolvimento motor', value: report.initialForm?.motorDevelopment },
                    { label: 'Sono da mãe', value: report.initialForm?.motherSleep }
                  ].map((item, i) => (
                    <div key={i} className="bg-[#FBF4FF] rounded-2xl p-4">
                      <p className="text-sm text-[#8F7AC5] font-medium mb-1">{item.label}</p>
                      <p className="text-[#30244F]">{item.value || '-'}</p>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </TabsContent>

          {/* Tab: Rotina */}
          <TabsContent value="rotina">
            <div className="space-y-6">
              {report.routineDays?.map((day, i) => (
                <motion.div
                  key={i}
                  className={`bg-white rounded-3xl p-6 shadow-sm ${!day.completed ? 'opacity-50' : ''}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#30244F] flex items-center gap-2">
                      <Sun className="w-5 h-5 text-[#F2ACAA]" />
                      Dia {day.day} {day.date && `- ${day.date}`}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm ${day.completed ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {day.completed ? 'Completo' : 'Pendente'}
                    </span>
                  </div>

                  {day.completed && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="bg-[#FBF4FF] rounded-xl p-3 text-center">
                          <p className="text-xs text-[#9890A8]">Acordou</p>
                          <p className="text-lg font-semibold text-[#30244F]">{day.wakeUpTime}</p>
                        </div>
                        <div className="bg-[#FBF4FF] rounded-xl p-3 text-center">
                          <p className="text-xs text-[#9890A8]">Dormiu</p>
                          <p className="text-lg font-semibold text-[#30244F]">{day.bedTime}</p>
                        </div>
                        <div className="bg-[#FBF4FF] rounded-xl p-3 text-center">
                          <p className="text-xs text-[#9890A8]">Despertares</p>
                          <p className="text-lg font-semibold text-[#30244F]">{day.nightWakings}x</p>
                        </div>
                        <div className="bg-[#FBF4FF] rounded-xl p-3 text-center">
                          <p className="text-xs text-[#9890A8]">Sonecas</p>
                          <p className="text-lg font-semibold text-[#30244F]">{day.naps?.length || 0}</p>
                        </div>
                      </div>

                      {day.naps && day.naps.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-[#6B5B8C] mb-2">Sonecas:</p>
                          <div className="flex flex-wrap gap-2">
                            {day.naps.map((nap, ni) => (
                              <div key={ni} className="bg-[#D98DAE]/10 px-3 py-2 rounded-lg text-sm">
                                <span className="text-[#8F7AC5] font-medium">{nap.startTime}</span>
                                <span className="text-[#9890A8]"> • {nap.duration}min • {nap.howSlept}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Atividades da Rotina (estruturadas) */}
                      {day.routineEntries && day.routineEntries.length > 0 && (
                        <div className="bg-[#FBF4FF] rounded-xl p-4">
                          <p className="text-sm font-medium text-[#6B5B8C] mb-3">Rotina do Dia:</p>
                          <div className="space-y-1">
                            {day.routineEntries
                              .sort((a, b) => (a.time || '').localeCompare(b.time || ''))
                              .map((entry, ei) => (
                                <div key={ei} className="flex gap-2 text-sm">
                                  <span className="font-semibold text-[#8F7AC5] min-w-[50px]">
                                    {entry.time || '--:--'}
                                  </span>
                                  <span className="text-[#30244F]">
                                    {entry.activity || entry.customActivity || '-'}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {day.routineDescription && (
                        <div className="bg-[#FBF4FF] rounded-xl p-4">
                          <p className="text-sm font-medium text-[#6B5B8C] mb-3">Descrição da rotina:</p>
                          <div className="text-[#30244F] text-sm">
                            {(() => {
                              const text = day.routineDescription;
                              
                              // Regex para encontrar horários em vários formatos
                              const timeRegex = /(?:(?:às|as|até|desde|de)\s+)?(\d{1,2})[h:](\d{2})?(?:\s*(?:h|hrs?|horas?))?/gi;
                              
                              // Primeiro, tentar o formato estruturado "HH:MM – Atividade"
                              const structuredRegex = /(\d{1,2}[h:]?\d{0,2})\s*[–-]\s*([^.]+)/g;
                              const structuredEntries = [];
                              let structuredMatch;
                              
                              while ((structuredMatch = structuredRegex.exec(text)) !== null) {
                                structuredEntries.push({ time: structuredMatch[1], activity: structuredMatch[2].trim() });
                              }
                              
                              if (structuredEntries.length >= 3) {
                                return (
                                  <div className="space-y-1">
                                    {structuredEntries.map((entry, idx) => (
                                      <div key={idx} className="flex gap-3">
                                        <span className="font-semibold text-[#8F7AC5] min-w-[55px]">{entry.time}</span>
                                        <span>{entry.activity}</span>
                                      </div>
                                    ))}
                                  </div>
                                );
                              }
                              
                              // Coletar todos os horários únicos
                              let match;
                              const allTimes = [];
                              const seenTimes = new Set();
                              
                              while ((match = timeRegex.exec(text)) !== null) {
                                const hour = match[1].padStart(2, '0');
                                const minute = match[2] || '00';
                                const formattedTime = `${hour}:${minute}`;
                                if (!seenTimes.has(formattedTime)) {
                                  seenTimes.add(formattedTime);
                                  allTimes.push({
                                    original: match[0],
                                    formatted: formattedTime,
                                    index: match.index,
                                    length: match[0].length
                                  });
                                }
                              }
                              
                              if (allTimes.length > 0) {
                                // Timeline resumida
                                const timeline = (
                                  <div className="mb-4 p-3 bg-white rounded-lg border border-[#8F7AC5]/20">
                                    <p className="text-xs text-[#6B5B8C] mb-2 font-medium">📍 Horários mencionados:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {allTimes.map((t, i) => (
                                        <span key={i} className="inline-flex items-center px-2 py-1 bg-[#8F7AC5]/10 text-[#8F7AC5] rounded-full text-xs font-semibold">
                                          {t.formatted}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                );
                                
                                // Dividir por frases (usando pontuação como delimitador)
                                // Preservar vírgulas dentro da frase, dividir apenas em . ! ?
                                const sentences = text.split(/(?<=[.!?])\s+/).filter(s => s.trim());
                                
                                // Função para destacar horários em uma frase
                                const highlightTimes = (sentence) => {
                                  // Regex apenas para horários simples (não capturar conectivos como parte do match)
                                  const localTimeRegex = /(\d{1,2})[h:](\d{2})?(?:\s*(?:h|hrs?|horas?))?/gi;
                                  let result = sentence;
                                  const matches = [];
                                  let m;
                                  
                                  while ((m = localTimeRegex.exec(sentence)) !== null) {
                                    const hour = m[1].padStart(2, '0');
                                    const minute = m[2] || '00';
                                    matches.push({
                                      original: m[0],
                                      formatted: `${hour}:${minute}`,
                                      index: m.index,
                                      length: m[0].length
                                    });
                                  }
                                  
                                  // Pegar o primeiro horário (pelo índice menor)
                                  const firstMatch = matches.length > 0 ? matches.reduce((a, b) => a.index < b.index ? a : b) : null;
                                  const firstTimeFormatted = firstMatch ? firstMatch.formatted : null;
                                  
                                  // Substituir de trás para frente para não afetar índices
                                  // Manter todos os horários destacados inline (não remover o primeiro)
                                  [...matches].sort((a, b) => b.index - a.index).forEach((mt) => {
                                    const before = result.substring(0, mt.index);
                                    const after = result.substring(mt.index + mt.length);
                                    result = before + `[[TIME:${mt.formatted}]]` + after;
                                  });
                                  
                                  return { text: result, hasTime: matches.length > 0, firstTime: firstTimeFormatted };
                                };
                                
                                return (
                                  <>
                                    {timeline}
                                    <div className="space-y-2">
                                      {sentences.map((sentence, idx) => {
                                        const { text: processedText, hasTime, firstTime } = highlightTimes(sentence);
                                        const parts = processedText.split(/\[\[TIME:(\d{2}:\d{2})\]\]/g);
                                        
                                        return (
                                          <div key={idx} className={`flex gap-3 items-start py-2 ${hasTime ? 'border-b border-[#8F7AC5]/10' : ''}`}>
                                            {hasTime && (
                                              <span className="inline-flex items-center px-2 py-1 bg-[#8F7AC5]/15 text-[#8F7AC5] rounded font-semibold text-xs whitespace-nowrap shrink-0">
                                                🕐 {firstTime}
                                              </span>
                                            )}
                                            <span className={`leading-relaxed ${!hasTime ? 'text-[#6B5B8C] italic' : ''}`}>
                                              {parts.map((part, pi) => {
                                                if (pi % 2 === 1) {
                                                  return (
                                                    <span key={pi} className="inline-flex items-center px-1.5 py-0.5 mx-0.5 bg-[#8F7AC5]/15 text-[#8F7AC5] rounded font-semibold text-xs">
                                                      {part}
                                                    </span>
                                                  );
                                                }
                                                return <span key={pi}>{part}</span>;
                                              })}
                                            </span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </>
                                );
                              }
                              
                              // Se não encontrou horários, mostrar texto normal
                              return <p className="whitespace-pre-wrap">{text}</p>;
                            })()}
                          </div>
                        </div>
                      )}

                      {day.importantNotes && (
                        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
                          <p className="text-sm font-medium text-yellow-700 mb-1">Observações:</p>
                          <p className="text-yellow-800 text-sm">{day.importantNotes}</p>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* Tab: Plano */}
          <TabsContent value="plano">
            {report.sleepPlan ? (
              // Plano já existe - mostrar resumo e opções
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                {/* Status do Plano */}
                <div className={`rounded-3xl p-6 ${report.status === 'approved' ? 'bg-green-50 border border-green-200' : 'bg-yellow-50 border border-yellow-200'}`}>
                  <div className="flex items-center gap-3 mb-4">
                    {report.status === 'approved' ? (
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    ) : (
                      <AlertCircle className="w-8 h-8 text-yellow-600" />
                    )}
                    <div>
                      <h3 className="text-lg font-semibold text-[#30244F]">
                        {report.status === 'approved' ? 'Plano Aprovado' : 'Plano em Rascunho'}
                      </h3>
                      <p className="text-sm text-[#6B5B8C]">
                        {report.status === 'approved' 
                          ? 'Este plano foi aprovado e está pronto para ser compartilhado.'
                          : 'Este plano ainda está em edição. Clique em "Editar Plano" para continuar.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Resumo do Plano */}
                <div className="bg-white rounded-3xl p-6 shadow-sm">
                  <h3 className="font-semibold text-[#30244F] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#8F7AC5]" />
                    Resumo do Plano
                  </h3>
                  <p className="text-[#6B5B8C] mb-4 line-clamp-4">
                    {report.sleepPlan.summary || 'Nenhum resumo disponível.'}
                  </p>
                  
                  {/* Pilares */}
                  {report.sleepPlan.pillars && report.sleepPlan.pillars.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-[#9890A8] mb-2">Pilares do plano:</p>
                      <div className="flex flex-wrap gap-2">
                        {report.sleepPlan.pillars.map((pillar, i) => (
                          <span 
                            key={i} 
                            className="px-3 py-1 rounded-full text-sm text-white"
                            style={{ backgroundColor: pillar.color || '#8F7AC5' }}
                          >
                            {pillar.title}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    className="bg-[#8F7AC5] hover:bg-[#30244F]"
                    onClick={() => navigate(`/admin/reports/${reportId}/plan`)}
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    {report.status === 'approved' ? 'Visualizar/Editar Plano' : 'Editar Plano'}
                  </Button>
                  
                  {report.status === 'approved' && (
                    <>
                      <Button
                        variant="outline"
                        className="border-[#6BA3D9] text-[#6BA3D9]"
                        onClick={() => {
                          const shareUrl = `${window.location.origin}/plan/${reportId}`;
                          navigator.clipboard.writeText(shareUrl);
                          alert('Link do plano copiado!');
                        }}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar Link do Plano
                      </Button>
                      <Button
                        variant="outline"
                        className="border-[#D98DAE] text-[#D98DAE]"
                        onClick={() => window.open(`/plan/${reportId}`, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Ver Página da Mãe
                      </Button>
                    </>
                  )}
                </div>
              </motion.div>
            ) : (
              // Plano não existe - mostrar opção de gerar
              <motion.div
                className="bg-white rounded-3xl p-8 shadow-sm text-center"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Sparkles className="w-16 h-16 text-[#D98DAE] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#30244F] mb-2">Plano ainda não gerado</h3>
                <p className="text-[#6B5B8C] mb-6">
                  Quando o registro de rotina estiver completo, você poderá gerar o plano de sono personalizado.
                </p>
                <Button
                  className="bg-[#8F7AC5] hover:bg-[#30244F]"
                  onClick={() => navigate(`/admin/reports/${reportId}/plan`)}
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Gerar Plano de Sono
                </Button>
              </motion.div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminReportDetailPage;
